import { useCallback, useRef, useState } from 'react';
import { uploadInlineDescriptionImage } from '../../functions/productFeedbackApi';

/** Mirrors the backend's per-image multer limit (ProductFeedbackController.ts). */
const MAX_INLINE_IMAGE_BYTES = 10 * 1024 * 1024;

interface UseInlineImageUploadOptions {
  /** Current markdown text. */
  value: string;
  /** Receives the text with the uploaded image's markdown spliced in. */
  onChange: (next: string) => void;
  /** The textarea the user is typing into — used to read/restore the caret position. */
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
}

/**
 * Adds GitHub-style inline image upload to a plain markdown textarea: pasting or dropping an
 * image uploads it and splices `![name](url)` in at the caret.
 *
 * While the upload is in flight a `![Uploading name…]()` placeholder holds the spot, so the
 * user can keep typing and still see where the image will land. On completion the placeholder
 * is swapped for the real markdown by string match rather than by the caret offset captured at
 * paste time — the caret has almost certainly moved by then, and with concurrent uploads
 * (paste two images quickly) every offset after the first insertion is stale. Matching on the
 * placeholder text keeps each upload anchored to its own spot regardless of completion order.
 */
export const useInlineImageUpload = ({ value, onChange, textareaRef }: UseInlineImageUploadOptions) => {
  const [uploadingCount, setUploadingCount] = useState(0);
  const [error, setError] = useState('');

  // `value` is captured per-render, but callbacks below run after an await — reading the prop
  // there would splice into a stale snapshot and drop any keystrokes typed during the upload.
  // A ref kept current every render gives them the live text.
  const valueRef = useRef(value);
  valueRef.current = value;

  // Distinguishes concurrent uploads' placeholders from each other.
  const uploadSeq = useRef(0);

  const uploadImage = useCallback(async (file: File) => {
    if (file.size > MAX_INLINE_IMAGE_BYTES) {
      setError(`"${file.name || 'Image'}" is too large. Maximum size is 10MB.`);
      return;
    }

    setError('');
    const seq = ++uploadSeq.current;
    const displayName = file.name || 'pasted image';
    const placeholder = `![Uploading ${displayName}… #${seq}]()`;

    // Splice the placeholder in at the caret (or append if the textarea isn't focused).
    const textarea = textareaRef.current;
    const current = valueRef.current;
    const caret = textarea ? textarea.selectionStart : current.length;
    const before = current.slice(0, caret);
    const after = current.slice(caret);
    // Keep the image on its own line — an image tag mid-sentence renders awkwardly, and this
    // matches what GitHub's own editor does when you paste into non-empty text.
    const prefix = before && !before.endsWith('\n') ? '\n' : '';
    const suffix = after && !after.startsWith('\n') ? '\n' : '';
    const insertion = `${prefix}${placeholder}${suffix}`;
    onChange(`${before}${insertion}${after}`);

    // Put the caret just past the inserted block so continued typing lands sensibly.
    const caretAfterInsert = caret + insertion.length;
    requestAnimationFrame(() => {
      if (!textareaRef.current) return;
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(caretAfterInsert, caretAfterInsert);
    });

    setUploadingCount(c => c + 1);
    try {
      const resp = await uploadInlineDescriptionImage(file);
      if (resp?.success && resp.data?.url) {
        const alt = (resp.data.name || displayName).replace(/[[\]]/g, '');
        onChange(valueRef.current.replace(placeholder, `![${alt}](${resp.data.url})`));
      } else {
        onChange(valueRef.current.replace(placeholder, ''));
        setError(resp?.message || 'Image upload failed. Please try again, or use the attachment picker.');
      }
    } catch (err: any) {
      onChange(valueRef.current.replace(placeholder, ''));
      setError(err?.message || 'Image upload failed. Please try again, or use the attachment picker.');
    } finally {
      setUploadingCount(c => c - 1);
    }
  }, [onChange, textareaRef]);

  // Typed against the element MUI's TextField actually attaches these to (the wrapper div),
  // not the inner textarea — the event only needs clipboardData/dataTransfer either way.
  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLElement>) => {
    // Browsers disagree about where a pasted image lands: some populate
    // clipboardData.files, others only clipboardData.items. Reading just one path
    // silently breaks pasting in a subset of browsers — the kind of bug that gets
    // reported as "paste doesn't work" with no reproduction steps. Check both and
    // de-duplicate, since a browser that populates both would otherwise upload twice.
    const fromFiles = Array.from(e.clipboardData?.files || []);
    const fromItems = Array.from(e.clipboardData?.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter((f): f is File => !!f);

    const seen = new Set<string>();
    // Only image items are intercepted — copying a screenshot alongside text (some tools put
    // both on the clipboard) must still paste the text normally.
    const imageFiles = [...fromFiles, ...fromItems].filter((f) => {
      if (!f.type.startsWith('image/')) return false;
      const key = `${f.name}:${f.size}:${f.lastModified}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (imageFiles.length === 0) return;
    e.preventDefault();
    imageFiles.forEach(uploadImage);
  }, [uploadImage]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLElement>) => {
    const imageFiles = Array.from(e.dataTransfer?.files || []).filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    e.preventDefault();
    imageFiles.forEach(uploadImage);
  }, [uploadImage]);

  return {
    isUploading: uploadingCount > 0,
    error,
    clearError: useCallback(() => setError(''), []),
    handlePaste,
    handleDrop,
  };
};
